<template>
  <div class="pricing-page">
    <!-- 导航栏 -->
    <nav class="top-nav">
      <div class="nav-container">
        <div class="nav-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span class="brand-text">{{ $t('nav.brand') }}</span>
        </div>
        <div class="nav-actions">
          <div class="language-switcher">
            <button 
              @click="toggleLanguage" 
              class="language-btn"
              :title="$t('common.language')"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M12.87,15.07L10.33,12.56L10.36,12.53C12.1,10.59 13.34,8.36 14.07,6H17V4H10V2H8V4H1V6H12.17C11.5,7.92 10.44,9.75 9,11.35C8.07,10.32 7.3,9.19 6.69,8H4.69C5.42,9.63 6.42,11.17 7.67,12.56L2.58,17.58L4,19L9,14L12.11,17.11L12.87,15.07M18.5,10H16.5L12,22H14L15.12,19H19.87L21,22H23L18.5,10M15.88,17L17.5,12.67L19.12,17H15.88Z"/>
              </svg>
              {{ currentLanguage === 'zh' ? 'EN' : '中' }}
            </button>
          </div>
          <router-link to="/" class="nav-btn">{{ $t('pricing.nav.home') }}</router-link>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <div class="main-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-content">
          <h1 class="hero-title">{{ $t('pricing.hero.title') }}</h1>
          <p class="hero-description">{{ $t('pricing.hero.description') }}</p>
        </div>
      </section>

      <!-- 定价卡片 -->
      <section class="pricing-section">
        <div class="pricing-container">
          <!-- 免费版 -->
          <div class="pricing-card free-card">
            <div class="card-header">
              <h3>{{ $t('pricing.plans.free.title') }}</h3>
              <div class="price">
                <span class="currency">$</span>
                <span class="amount">0</span>
                <span class="period">{{ $t('pricing.plans.free.period') }}</span>
              </div>
            </div>
            <div class="card-features">
              <ul>
                <li v-for="feature in $t('pricing.plans.free.features')" :key="feature">
                  <q-icon name="check" class="feature-icon" />
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div class="card-action">
              <router-link to="/" class="btn btn-outline">{{ $t('pricing.plans.free.cta') }}</router-link>
            </div>
          </div>

          <!-- 企业版 -->
          <div class="pricing-card enterprise-card featured">
            <div class="featured-badge">{{ $t('pricing.featured') }}</div>
            <div class="card-header">
              <h3>{{ $t('pricing.plans.enterprise.title') }}</h3>
              <div class="price">
                <span class="currency">$</span>
                <span class="amount">39.9</span>
                <span class="period">{{ $t('pricing.plans.enterprise.period') }}</span>
              </div>
            </div>
            <div class="card-features">
              <ul>
                <li v-for="feature in $t('pricing.plans.enterprise.features')" :key="feature">
                  <q-icon name="check" class="feature-icon" />
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div class="card-action">
              <button @click="redirectToStripe" class="btn btn-primary">
                {{ $t('pricing.plans.enterprise.cta') }}
              </button>
            </div>
          </div>

          <!-- 定制版 -->
          <div class="pricing-card custom-card">
            <div class="card-header">
              <h3>{{ $t('pricing.plans.custom.title') }}</h3>
              <div class="price">
                <span class="contact-price">{{ $t('pricing.plans.custom.price') }}</span>
              </div>
            </div>
            <div class="card-features">
              <ul>
                <li v-for="feature in $t('pricing.plans.custom.features')" :key="feature">
                  <q-icon name="check" class="feature-icon" />
                  {{ feature }}
                </li>
              </ul>
            </div>
            <div class="card-action">
              <button @click="contactUs" class="btn btn-outline">{{ $t('pricing.plans.custom.cta') }}</button>
            </div>
          </div>
        </div>
      </section>

      <!-- 特性说明 -->
      <section class="features-section">
        <div class="features-container">
          <h2>{{ $t('pricing.features.title') }}</h2>
          <div class="features-grid">
            <div class="feature-item">
              <div class="feature-icon-container">
                <q-icon name="cloud_off" size="3rem" />
              </div>
              <h4>{{ $t('pricing.features.private.title') }}</h4>
              <p>{{ $t('pricing.features.private.description') }}</p>
            </div>
            <div class="feature-item">
              <div class="feature-icon-container">
                <q-icon name="security" size="3rem" />
              </div>
              <h4>{{ $t('pricing.features.secure.title') }}</h4>
              <p>{{ $t('pricing.features.secure.description') }}</p>
            </div>
            <div class="feature-item">
              <div class="feature-icon-container">
                <q-icon name="speed" size="3rem" />
              </div>
              <h4>{{ $t('pricing.features.performance.title') }}</h4>
              <p>{{ $t('pricing.features.performance.description') }}</p>
            </div>
            <div class="feature-item">
              <div class="feature-icon-container">
                <q-icon name="support" size="3rem" />
              </div>
              <h4>{{ $t('pricing.features.support.title') }}</h4>
              <p>{{ $t('pricing.features.support.description') }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 联系信息 -->
      <section class="contact-section">
        <div class="contact-container">
          <h2>{{ $t('pricing.contact.title') }}</h2>
          <p>{{ $t('pricing.contact.description') }}</p>
          <div class="contact-info">
            <div class="contact-item">
              <q-icon name="email" />
              <span>fn9170@gmail.com</span>
            </div>
            <div class="contact-item">
              <q-icon name="phone" />
              <span>+853 6539 6990 (WhatsApp)</span>
            </div>
            <div class="contact-item">
              <q-icon name="schedule" />
              <span>{{ $t('pricing.contact.responseTime') }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 页脚 -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-content">
          <div class="footer-brand">
            <span>{{ $t('footer.brand') }}</span>
          </div>
          <div class="footer-links">
            <span>{{ $t('footer.contact') }}</span>
          </div>
          <div class="footer-copyright">
            <span>{{ $t('footer.copyright') }}</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { changeLanguage, getCurrentLanguage } from '@/i18n'

export default {
  name: 'PricingPage',
  data() {
    return {
      currentLanguage: getCurrentLanguage()
    }
  },
  methods: {
    toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'zh' : 'en'
      this.currentLanguage = changeLanguage(newLanguage)
      this.updateSEO()
    },
    
    updateSEO() {
      // 更新页面标题
      document.title = this.$t('pricing.seo.title')
      
      // 更新meta标签
      const updateMetaTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('name', name)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      updateMetaTag('description', this.$t('pricing.seo.description'))
      updateMetaTag('keywords', this.$t('pricing.seo.keywords'))
    },

    redirectToStripe() {
      // TODO: 替换为你的实际 Stripe 支付链接
      // 1. 在 Stripe Dashboard 中创建支付链接
      // 2. 设置产品：DXF Viewer - Private Deployment ($39)
      // 3. 复制支付链接并替换下面的URL
      // 详细设置说明请参考 STRIPE-SETUP.md 文件
      const stripeUrl = 'https://buy.stripe.com/cNicN59b42cP5OY9gu6sw01'
      window.open(stripeUrl, '_blank')
      
      // 支付成功后，客户将收到包含以下内容的邮件：
      // - Docker部署包下载链接
      // - 安装和配置文档
      // - 许可证密钥
      // - 技术支持联系方式
    },

    contactUs() {
      // WhatsApp联系方式
      const phoneNumber = '85365396990' // +853 6539 6990 (去掉+和空格)
      const message = encodeURIComponent(this.$t('pricing.contact.whatsappMessage'))
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`
      window.open(whatsappUrl, '_blank')
    }
  },

  mounted() {
    this.updateSEO()
  },

  watch: {
    '$i18n.locale'() {
      this.updateSEO()
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.pricing-page {
  min-height: 100vh;
  background: #0f172a;
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* 导航栏样式 */
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
}

.brand-icon {
  width: 32px;
  height: 32px;
  color: #3b82f6;
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.language-btn:hover {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.nav-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.3s ease;
  text-decoration: none;
}

.nav-btn:hover {
  color: white;
}

/* 主要内容 */
.main-content {
  padding-top: 70px;
}

/* Hero Section */
.hero-section {
  padding: 80px 20px;
  text-align: center;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-description {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
}

/* 定价区域 */
.pricing-section {
  padding: 80px 20px;
}

.pricing-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 32px;
}

.pricing-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 40px 32px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.pricing-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(59, 130, 246, 0.1);
}

.featured {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.featured-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #3b82f6;
  color: white;
  padding: 4px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
}

.card-header h3 {
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: white;
}

.price {
  margin-bottom: 32px;
}

.currency {
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.6);
  vertical-align: top;
}

.amount {
  font-size: 3rem;
  font-weight: 800;
  color: white;
}

.period {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 8px;
}

.contact-price {
  font-size: 1.5rem;
  color: #3b82f6;
  font-weight: 600;
}

.card-features ul {
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
}

.card-features li {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  color: rgba(255, 255, 255, 0.8);
}

.feature-icon {
  color: #22c55e;
  font-size: 1.25rem;
}

.btn {
  display: inline-block;
  padding: 12px 32px;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  border: none;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

/* 特性区域 */
.features-section {
  padding: 80px 20px;
  background: rgba(255, 255, 255, 0.02);
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.features-container h2 {
  font-size: 2.5rem;
  margin-bottom: 64px;
  color: white;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
}

.feature-item {
  text-align: center;
}

.feature-icon-container {
  margin-bottom: 24px;
  color: #3b82f6;
}

.feature-item h4 {
  font-size: 1.25rem;
  margin-bottom: 16px;
  color: white;
}

.feature-item p {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
}

/* 联系区域 */
.contact-section {
  padding: 80px 20px;
}

.contact-container {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.contact-container h2 {
  font-size: 2.5rem;
  margin-bottom: 24px;
  color: white;
}

.contact-container p {
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 48px;
}

.contact-info {
  display: flex;
  justify-content: center;
  gap: 48px;
  flex-wrap: wrap;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.125rem;
}

.contact-item .q-icon {
  color: #3b82f6;
  font-size: 1.5rem;
}

/* 页脚 */
.footer {
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 40px 20px;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.footer-brand {
  font-weight: 600;
  color: white;
}

.footer-links,
.footer-copyright {
  color: rgba(255, 255, 255, 0.6);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }
  
  .pricing-container {
    grid-template-columns: 1fr;
  }
  
  .features-grid {
    grid-template-columns: 1fr;
  }
  
  .contact-info {
    flex-direction: column;
    gap: 24px;
  }
  
  .footer-content {
    flex-direction: column;
    text-align: center;
  }
  
  .nav-actions {
    gap: 16px;
  }
}
</style>
